import axios from "axios";
import { toast } from "sonner";

const CONFIGURED_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const isPreviewHost = (host) => host.includes("preview.emergentagent.com") || host.includes("localhost");
const currentOrigin = typeof window !== "undefined" ? window.location.origin : "";
const currentHost = typeof window !== "undefined" ? window.location.hostname : "";
const BACKEND_URL = (!isPreviewHost(currentHost) && currentOrigin) ? currentOrigin : CONFIGURED_BACKEND_URL;
export const API_ROOT = `${BACKEND_URL}/api`;
const SESSION_TOKEN_KEY = "hosthelp.session.token";
// Tab-scoped key — set when super-admin impersonates an owner. Reading
// sessionStorage takes precedence over localStorage so the impersonated tab
// uses the owner token without kicking the super-admin out of their own tab.
const IMPERSONATION_TOKEN_KEY = "hosthelp.impersonation.token";
const MAX_RETRIES = 2;
const RETRY_BACKOFF_MS = [3000, 6000];
const RETRYABLE_STATUSES = new Set([502, 503, 504, 520, 521, 522, 524]);
const FAST_RETRY_BACKOFF_MS = [1000, 2500];

// Migration: move old token key to new one
(function migrateTokenKey() {
  const old = window.localStorage.getItem("hotelops.session.token");
  if (old && !window.localStorage.getItem(SESSION_TOKEN_KEY)) {
    window.localStorage.setItem(SESSION_TOKEN_KEY, old);
    window.localStorage.removeItem("hotelops.session.token");
  }
})();

// Bootstrap: if the URL hash carries an impersonation token (e.g.
// `/owner#imp_token=xxx`), stash it in sessionStorage so this tab uses it as
// the active session WITHOUT touching localStorage. Then strip the hash so
// the token never lingers in the address bar / history.
(function bootstrapImpersonation() {
  if (typeof window === "undefined" || !window.location.hash) return;
  const m = window.location.hash.match(/[#&]imp_token=([^&]+)/);
  if (!m) return;
  try {
    window.sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, decodeURIComponent(m[1]));
    // Strip the hash without reloading
    const clean = window.location.pathname + window.location.search;
    window.history.replaceState(null, "", clean);
  } catch { /* sessionStorage unavailable — silently ignore */ }
})();

export const api = axios.create({
  baseURL: API_ROOT,
  timeout: 12000,
});

export function getSessionToken() {
  // Per-tab impersonation overrides the regular session token.
  try {
    const imp = window.sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);
    if (imp) return imp;
  } catch { /* sessionStorage unavailable */ }
  return window.localStorage.getItem(SESSION_TOKEN_KEY);
}

export function setSessionToken(token) {
  window.localStorage.setItem(SESSION_TOKEN_KEY, token);
}

export function clearSessionToken() {
  // If this tab is in impersonation mode, only clear the impersonation token
  // so the super-admin's real session in their other tab(s) keeps working.
  try {
    if (window.sessionStorage.getItem(IMPERSONATION_TOKEN_KEY)) {
      window.sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
      return;
    }
  } catch { /* sessionStorage unavailable */ }
  window.localStorage.removeItem(SESSION_TOKEN_KEY);
}

/** True when the current tab is running on an impersonation token. */
export function isImpersonating() {
  try { return !!window.sessionStorage.getItem(IMPERSONATION_TOKEN_KEY); } catch { return false; }
}

api.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Track retry count
  config._retryCount = config._retryCount || 0;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;

    // Handle blob response errors
    if (error?.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        error.response.data = json;
      } catch {
        error.response.data = { detail: "Request failed" };
      }
    }

    const status = error?.response?.status;

    const retryAfterSeconds = Number(error?.response?.headers?.["retry-after"] || 0);
    const isRetryable = !status || RETRYABLE_STATUSES.has(status);

    // Auto-retry transient network/server failures. 503 gets 2 attempts with
    // 3s/6s backoff (or server Retry-After when provided) so short Mongo pool
    // pauses do not immediately surface to guests/staff.
    if (config && config._retryCount < MAX_RETRIES && isRetryable) {
      config._retryCount += 1;
      const retrySchedule = config._fastRetry ? FAST_RETRY_BACKOFF_MS : RETRY_BACKOFF_MS;
      const backoffMs = retryAfterSeconds > 0
        ? retryAfterSeconds * 1000
        : retrySchedule[config._retryCount - 1] || retrySchedule[retrySchedule.length - 1];
      await new Promise((r) => setTimeout(r, backoffMs));
      return api(config);
    }

    // Show user-friendly toasts for common errors
    if (status === 429) {
      const isGuestPublicFlow = String(config?.url || "").includes("/public/");
      if (!isGuestPublicFlow) {
        toast.error("Too many requests. Please wait a moment.", { id: "rate-limit" });
      }
    } else if (status === 503 || status === 520 || status === 521 || status === 522 || status === 524) {
      toast.error("Server is temporarily busy. Please try again in a moment.", { id: "server-down" });
    } else if (!status && error?.code === "ERR_NETWORK") {
      toast.error("Network error. Please check your connection.", { id: "network-error" });
    }

    return Promise.reject(error);
  }
);

export function extractErrorMessage(error) {
  try {
    const detail = error?.response?.data?.detail;
    if (typeof detail === "string") {
      return detail;
    }
    // FastAPI 422 — `detail` is an array of {loc, msg, type}
    if (Array.isArray(detail) && detail.length > 0) {
      const human = detail.slice(0, 3).map((d) => {
        const path = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
        const msg = (d?.msg || "").replace(/^Value error,?\s*/i, "");
        return path ? `${path}: ${msg}` : msg;
      }).filter(Boolean).join("; ");
      if (human) return human;
    }
    if (detail?.message) {
      return detail.message;
    }
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
  } catch {
    // blob responses may throw when accessing data properties
  }
  return error?.message || "Something went wrong";
}

export function roleHomeRoute(role) {
  if (role === "super_admin") return "/super-admin";
  if (role === "owner") return "/owner";
  if (role === "staff") return "/staff";
  return "/login";
}
